import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "@/components/ui/command";
import {
  ArrowLeft, Clapperboard, Users, Package, Video,
  Sparkles, BookOpen,
  UserCircle, Film, Minus, Plus, Clock, Check, ChevronDown,
} from "lucide-react";
import type { Persona, Product, NarrativeAngle, PersonaCharacter } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";

import { useToast } from "@/hooks/use-toast";

export default function PixarCampaignCreatePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const nameEditedRef = useRef(false);
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [narrativeAngleId, setNarrativeAngleId] = useState<string | null>(null);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  // Ad length: pixar now renders MORE 9-panel storyboards for a longer ad,
  // not longer clips. Each storyboard is a fixed 36s (9 × 4s SeeDance clips), so
  // 36s = 1 storyboard, 72s = 2, 108s = 3, all rendered on KIE SeeDance 2.0.
  const [clipTotalSeconds, setClipTotalSeconds] = useState(36);
  const [videoCount, setVideoCount] = useState(3);
  const [avatarCount, setAvatarCount] = useState(1);
  const [angleMode, setAngleMode] = useState<"saved" | "custom">("custom");
  const [concept, setConcept] = useState("");
  const [savedCampaignId, setSavedCampaignId] = useState<string | null>(null);
  const wantNavigateRef = useRef(false);
  const creatingRef = useRef(false);

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: "always",
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: "always",
  });

  const { data: personaCharacters, isLoading: loadingCharacters } = useQuery<PersonaCharacter[]>({
    queryKey: [`/api/personas/${personaId}/characters`],
    enabled: !!personaId,
  });

  useEffect(() => {
    setSelectedCharacterIds([]);
  }, [personaId]);

  const { data: narrativeAngles, isLoading: loadingAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`, { persona_id: personaId, product_id: productId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (personaId) params.set("persona_id", personaId);
      if (productId) params.set("product_id", productId);
      const res = await fetch(`/api/brands/${brandId}/narrative-angles?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch narrative angles");
      return res.json();
    },
    enabled: !!brandId && !!personaId && !!productId,
  });

  const selectedPersona = personaId ? personas?.find(p => p.id === personaId) : null;
  const selectedProduct = productId ? products?.find(p => p.id === productId) : null;
  const activeAngles = (narrativeAngles || []).filter(a => !a.archivedAt);
  const selectedAngle = narrativeAngleId ? activeAngles.find(a => a.id === narrativeAngleId) : null;

  const [suggestingName, setSuggestingName] = useState(false);
  useEffect(() => {
    if (nameEditedRef.current) return;
    const personaName = selectedPersona?.name;
    const productName = selectedProduct?.name;
    const angleName = selectedAngle?.name;
    if (!personaName && !productName && !angleName) {
      setName("");
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        setSuggestingName(true);
        const res = await fetch(`/api/brands/${brandId}/pixar-campaigns/suggest-name`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          signal: ctrl.signal,
          body: JSON.stringify({ personaName, productName, angleName }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!nameEditedRef.current && data?.name) setName(data.name);
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          const parts = [personaName, productName, angleName].filter(Boolean) as string[];
          const date = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          if (!nameEditedRef.current) setName(`${parts.join(" × ")} — ${date}`);
        }
      } finally {
        setSuggestingName(false);
      }
    }, 600);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [brandId, selectedPersona?.id, selectedProduct?.id, selectedAngle?.id]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/pixar-campaigns`, data);
      return res.json();
    },
    onSuccess: (campaign) => {
      setSavedCampaignId(campaign.id);
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "pixar-campaigns"] });
      if (wantNavigateRef.current) {
        wantNavigateRef.current = false;
        toast({ title: "Pixar campaign created" });
        navigate(`/brands/${brandId}/pixar-campaigns/${campaign.id}`);
      }
    },
    onError: (e: any) => {
      wantNavigateRef.current = false;
      toast({ title: "Failed to create campaign", description: e.message, variant: "destructive" });
    },
    onSettled: () => { creatingRef.current = false; },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("PATCH", `/api/pixar-campaigns/${savedCampaignId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "pixar-campaigns"] });
    },
  });

  const angleReady = angleMode === "saved" ? !!narrativeAngleId : !!concept.trim();

  const allReady =
    !!personaId &&
    !!productId &&
    angleReady &&
    videoCount >= 1 &&
    avatarCount >= 1 &&
    !!name.trim();

  function buildPayload() {
    const usingCustom = angleMode === "custom";
    const count = Math.max(1, selectedCharacterIds.length);
    return {
      name: name.trim(),
      adUnitTypeId: null,
      personaId: personaId || null,
      productId: productId || null,
      narrativeAngleId: usingCustom ? null : (narrativeAngleId || null),
      customConcept: usingCustom ? concept.trim() : null,
      videoStyle: "Pixar",
      duration: `${clipTotalSeconds}s`,
      secondsPerScene: Math.round(clipTotalSeconds / 9),
      videoCount: count,
      avatarCount: count,
      campaignMode: "oneshot",
      concept: usingCustom ? (concept.trim() || null) : null,
      targetAudience: null,
      notes: selectedCharacterIds.length > 0 ? { selectedCharacterIds } : null,
    };
  }

  function startSave() {
    if (savedCampaignId || creatingRef.current || createMutation.isPending) return;
    creatingRef.current = true;
    createMutation.mutate(buildPayload());
  }

  function handleCreate() {
    if (!name.trim()) {
      toast({ title: "Campaign name is required", variant: "destructive" });
      return;
    }
    if (savedCampaignId) {
      navigate(`/brands/${brandId}/pixar-campaigns/${savedCampaignId}`);
      return;
    }
    // Explicit click: navigate as soon as the save resolves (whether it's this
    // call or an auto-save already in flight).
    wantNavigateRef.current = true;
    if (creatingRef.current || createMutation.isPending) return;
    creatingRef.current = true;
    createMutation.mutate(buildPayload());
  }

  // Auto-save the campaign as a draft once every required input is filled in, so
  // a started campaign is never lost — even if the user leaves before clicking
  // "Create Campaign". Fires once; later edits sync via updateMutation.
  useEffect(() => {
    if (!allReady || savedCampaignId) return;
    const t = setTimeout(() => { startSave(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allReady, savedCampaignId, name, personaId, productId, narrativeAngleId, angleMode, concept, clipTotalSeconds, selectedCharacterIds]);

  // Keep the saved draft in sync with later tweaks. The narrative angle is only
  // re-synced for saved angles — a custom concept is transformed once at save
  // time and we never re-run that (expensive) transform on edits.
  useEffect(() => {
    if (!savedCampaignId) return;
    const t = setTimeout(() => {
      const usingCustom = angleMode === "custom";
      const count = Math.max(1, selectedCharacterIds.length);
      const update: any = {
        name: name.trim(),
        adUnitTypeId: null,
        personaId: personaId || null,
        productId: productId || null,
        videoStyle: "Pixar",
        duration: `${clipTotalSeconds}s`,
        secondsPerScene: Math.round(clipTotalSeconds / 9),
        videoCount: count,
        avatarCount: count,
        notes: selectedCharacterIds.length > 0 ? { selectedCharacterIds } : null,
      };
      if (!usingCustom) update.narrativeAngleId = narrativeAngleId || null;
      updateMutation.mutate(update);
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCampaignId, name, personaId, productId, narrativeAngleId, angleMode, clipTotalSeconds, selectedCharacterIds]);

  const personaOptions = (personas || []).map((p) => ({
    value: p.id,
    label: p.name,
    imageUrl: (p as any).imageUrl || null,
    description: p.awarenessStage ? p.awarenessStage.replace(/_/g, " ") : (p.summary || null),
  }));

  const productOptions = (products || []).map((p) => {
    const imgs = Array.isArray(p.adgenImageRefs) ? (p.adgenImageRefs as string[]) : [];
    return {
      value: p.id,
      label: p.name,
      imageUrl: imgs[0] || null,
      description: p.category || p.summary || p.description || null,
    };
  });

  const angleOptions = activeAngles.map((a) => ({
    value: a.id,
    label: a.name,
    imageUrl: null,
    description: a.family ? `${a.family}${a.summary ? " · " + a.summary : ""}` : (a.summary || null),
  }));

  return (
    <div className="min-h-full">
      <div className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-8 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => navigate(`/brands/${brandId}/pixar-campaigns`)}
            data-testid="button-back-pixar-create"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="h-5 w-px bg-border/50" />
          <Clapperboard className="h-4 w-4 text-violet-500" />
          <span className="text-sm font-semibold">New Pixar Campaign</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-6 space-y-5">
        <div>
          <div className="flex items-center gap-2 group">
            <Clapperboard className="h-5 w-5 text-violet-500 shrink-0" />
            <Input
              value={name}
              onChange={(e) => { nameEditedRef.current = true; setName(e.target.value); }}
              placeholder="New Pixar Campaign"
              className="h-auto px-1 py-0.5 text-xl font-bold tracking-tight border-0 bg-transparent shadow-none rounded-md hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-1 focus-visible:ring-primary/30 focus-visible:ring-offset-0 placeholder:text-foreground/40"
              data-testid="input-pixar-name"
            />
            {suggestingName && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0" data-testid="text-suggesting-name">
                <Sparkles className="h-3 w-3 animate-pulse" /> Naming…
              </span>
            )}
            {!suggestingName && savedCampaignId && (
              <span className="text-[11px] flex items-center gap-1 shrink-0" data-testid="text-draft-saved">
                {createMutation.isPending || updateMutation.isPending ? (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Sparkles className="h-3 w-3 animate-pulse" /> Saving…
                  </span>
                ) : (
                  <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Draft saved
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        <Card className="border-border/40">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">Set your inputs</p>
                <p className="text-xs text-muted-foreground">Persona and product are both required.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PixarPicker
                icon={<Users className="h-4 w-4" />}
                label="Persona"
                placeholder={personaOptions.length === 0 ? "No personas yet" : "Choose persona"}
                value={personaId || ""}
                onChange={(v) => { setPersonaId(v); setNarrativeAngleId(null); }}
                options={personaOptions}
                testId="select-pixar-persona"
                disabled={personaOptions.length === 0}
              />
              <PixarPicker
                icon={<Package className="h-4 w-4" />}
                label="Product"
                placeholder={productOptions.length === 0 ? "No products yet" : "Choose product"}
                value={productId || ""}
                onChange={(v) => { setProductId(v); setNarrativeAngleId(null); }}
                options={productOptions}
                testId="select-pixar-product"
                disabled={productOptions.length === 0}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label className="text-xs font-medium">Ad Length</Label>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[36, 72, 108].map((total) => {
                  const active = clipTotalSeconds === total;
                  return (
                    <button
                      key={total}
                      type="button"
                      onClick={() => setClipTotalSeconds(total)}
                      className={[
                        "rounded-xl border px-3 py-2.5 text-left transition-all",
                        active
                          ? "border-primary ring-2 ring-primary/40 bg-primary/5 shadow-sm"
                          : "border-border/50 hover:border-primary/40 hover:shadow-sm",
                      ].join(" ")}
                      data-testid={`button-ad-length-${total}`}
                    >
                      <p className="text-sm font-semibold">{total}s</p>
                      <p className="text-[11px] text-muted-foreground">{total / 36} storyboard{total === 36 ? "" : "s"} · {total / 4} scenes × 4s</p>
                    </button>
                  );
                })}
              </div>
            </div>

          </CardContent>
        </Card>

        {personaId && (
          <Card className="border-border/40">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <UserCircle className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">Select your models</p>
                  <p className="text-xs text-muted-foreground">
                    {loadingCharacters
                      ? "Loading characters…"
                      : (personaCharacters?.length || 0) === 0
                        ? `No characters yet for ${selectedPersona?.name || "this persona"}. Add some on the persona page.`
                        : `Pick which characters from ${selectedPersona?.name || "this persona"} to feature.`}
                  </p>
                </div>
                {selectedCharacterIds.length > 0 && (
                  <Badge variant="secondary" className="text-[11px]" data-testid="badge-selected-character-count">
                    {selectedCharacterIds.length} selected
                  </Badge>
                )}
              </div>

              {!loadingCharacters && (personaCharacters?.length || 0) > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2">
                  {(personaCharacters || []).map((c) => {
                    const isSelected = selectedCharacterIds.includes(c.id);
                    const ready = c.avatarStatus === "ready" && !!c.avatarUrl;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedCharacterIds((prev) =>
                            prev.includes(c.id) ? prev.filter((id) => id !== c.id) : [...prev, c.id]
                          );
                        }}
                        className={[
                          "group relative rounded-xl overflow-hidden border text-left transition-all",
                          isSelected
                            ? "border-primary ring-2 ring-primary/40 shadow-sm"
                            : "border-border/50 hover:border-primary/40 hover:shadow-sm",
                        ].join(" ")}
                        data-testid={`character-card-${c.id}`}
                      >
                        <div className="relative aspect-square bg-muted/40 overflow-hidden">
                          {ready ? (
                            <img
                              src={c.avatarUrl!}
                              alt={c.name}
                              className="absolute inset-0 w-full h-full object-cover block"
                              loading="lazy"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <UserCircle className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                          )}
                        </div>
                        <div className="px-1.5 py-1 bg-background">
                          <p className="text-[11px] font-semibold leading-tight break-words" title={c.name}>{c.name}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center shadow">
                            ✓
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-border/40">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <BookOpen className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">Narrative angle</p>
                <p className="text-xs text-muted-foreground">Pick a saved angle, or write a custom concept we'll turn into one.</p>
              </div>
            </div>

            <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5">
              <button
                type="button"
                onClick={() => setAngleMode("saved")}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  angleMode === "saved"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="button-angle-mode-saved"
              >
                <BookOpen className="h-3.5 w-3.5" /> Saved angle
              </button>
              <button
                type="button"
                onClick={() => setAngleMode("custom")}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                  angleMode === "custom"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
                data-testid="button-angle-mode-custom"
              >
                <Sparkles className="h-3.5 w-3.5" /> Custom concept
              </button>
            </div>

            {angleMode === "saved" ? (
              <PixarPicker
                icon={<BookOpen className="h-4 w-4" />}
                label="Narrative Angle"
                placeholder={
                  !personaId || !productId
                    ? "Pick persona & product first"
                    : loadingAngles
                      ? "Loading angles…"
                      : angleOptions.length === 0
                        ? "No saved angles for this pair"
                        : "Choose narrative angle"
                }
                value={narrativeAngleId || ""}
                onChange={(v) => setNarrativeAngleId(v)}
                options={angleOptions}
                testId="select-pixar-angle"
                disabled={!personaId || !productId || loadingAngles || angleOptions.length === 0}
              />
            ) : (
              <div>
                <Label htmlFor="pixar-concept" className="text-sm font-medium">Custom concept</Label>
                <p className="text-xs text-muted-foreground mb-1.5">Describe the moment, idea, or angle. On create, we'll transform it into a saved narrative angle for this persona &amp; product.</p>
                <Textarea
                  id="pixar-concept"
                  placeholder="e.g., A woman realizes her deodorant still works after an intense gym session…"
                  value={concept}
                  onChange={(e) => setConcept(e.target.value)}
                  rows={4}
                  className="resize-none"
                  data-testid="input-pixar-concept"
                />
              </div>
            )}

          </CardContent>
        </Card>


        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={() => navigate(`/brands/${brandId}/pixar-campaigns`)}
            data-testid="button-cancel"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <Button
            className="rounded-full px-6"
            onClick={handleCreate}
            disabled={!allReady || createMutation.isPending}
            data-testid="button-create-pixar-campaign-footer"
          >
            {createMutation.isPending
              ? (angleMode === "custom" ? "Generating angle…" : "Saving…")
              : savedCampaignId
                ? "Open Campaign"
                : "Create Campaign"}
          </Button>
        </div>

      </div>
    </div>
  );
}

function PixarPicker({
  icon, label, placeholder, value, onChange, options, testId, disabled,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; imageUrl?: string | null; description?: string | null }>;
  testId: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const commandRef = useRef<HTMLDivElement>(null);
  const filled = !!value;
  const hasAvatars = options.some((o) => !!o.imageUrl);
  const selected = filled ? options.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  // Show a search box once the list gets long enough to be worth filtering.
  const showSearch = options.length > 5;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          disabled={disabled}
          className={[
            "group flex w-full items-center min-h-[72px] rounded-xl border px-3 py-2.5 text-sm transition-all duration-150 outline-none",
            "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            filled
              ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02]"
              : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
          ].join(" ")}
        >
          <div className="flex items-center gap-3 w-full min-w-0">
            {filled && selected?.imageUrl ? (
              <img
                src={selected.imageUrl}
                alt=""
                className="h-10 w-10 rounded-lg object-cover shrink-0 ring-1 ring-primary/20 shadow-sm"
                loading="lazy"
              />
            ) : (
              <div
                className={[
                  "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
                  filled
                    ? "bg-primary/15 text-primary ring-primary/20"
                    : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
                ].join(" ")}
              >
                {icon}
              </div>
            )}
            <div className="flex flex-col items-start min-w-0 flex-1 text-left">
              <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
                {label}
                {!filled && <span className="text-muted-foreground/40">*</span>}
              </span>
              {filled && selected ? (
                <div className="flex flex-col w-full min-w-0">
                  <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                    {selected.label}
                  </span>
                  {selectedDescription && (
                    <span className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5">
                      {selectedDescription}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                  {placeholder}
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 min-w-[var(--radix-popover-trigger-width)] w-[min(28rem,calc(100vw-2rem))]"
        onOpenAutoFocus={(e) => {
          // Without a CommandInput there is no tabbable child for Radix's
          // FocusScope, so cmdk's keydown handler never receives focus and
          // arrow/Enter navigation breaks. Focus the Command root directly.
          if (!showSearch) {
            e.preventDefault();
            commandRef.current?.focus();
          }
        }}
      >
        <Command ref={commandRef}>
          {showSearch && (
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} data-testid={`${testId}-search`} />
          )}
          <CommandList>
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            {options.map((o, idx) => {
              const desc = o.description?.trim() || "";
              const isSelected = o.value === value;
              return (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${desc} ${idx}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  className="gap-2"
                  data-testid={`${testId}-option-${o.value}`}
                >
                  {hasAvatars ? (
                    o.imageUrl ? (
                      <img src={o.imageUrl} alt="" className="h-6 w-6 rounded object-cover shrink-0 border border-border/40" loading="lazy" />
                    ) : (
                      <div className="h-6 w-6 rounded bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                        {o.label.trim().charAt(0).toUpperCase() || "?"}
                      </div>
                    )
                  ) : null}
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-xs whitespace-normal break-words leading-tight">{o.label}</span>
                    {desc && (
                      <span className="text-[11px] text-muted-foreground/80 truncate leading-tight mt-0.5">{desc}</span>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CounterCard({
  icon, iconBg, title, subtitle, value, min, max, onChange, testId,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  testId: string;
}) {
  return (
    <Card className="border-border/40">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>{icon}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">{title}</p>
              <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-0 shrink-0" data-testid={testId}>
            <button
              type="button"
              className="w-8 h-8 rounded-l-lg border border-r-0 border-border/60 flex items-center justify-center hover:bg-muted/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => onChange(Math.max(min, value - 1))}
              disabled={value <= min}
              aria-label={`Decrease ${title.toLowerCase()}`}
              data-testid={`${testId}-minus`}
            >
              <Minus className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <div className="w-10 h-8 border-y border-border/60 flex items-center justify-center bg-muted/10">
              <span className="text-sm font-bold tabular-nums">{value}</span>
            </div>
            <button
              type="button"
              className="w-8 h-8 rounded-r-lg border border-l-0 border-border/60 flex items-center justify-center hover:bg-muted/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => onChange(Math.min(max, value + 1))}
              disabled={value >= max}
              aria-label={`Increase ${title.toLowerCase()}`}
              data-testid={`${testId}-plus`}
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
